import React, { useEffect, useMemo, useState } from "react";
import { FiHeart, FiChevronLeft, FiChevronRight, FiCheck, FiEdit2, FiTrash2 } from "react-icons/fi";
import { FaHeart } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import "bootstrap/dist/css/bootstrap.min.css";
import "./productDetail.css";
import FormModal from "../PopupModal/FormModal";
import LoginModal from "./Login/Login";
import ConfigureRentalModal, { getPackageForPlan, ALL_PLANS } from "./ConfigureRentalModal";
import pm from "./images/products.webp";
import axiosInstance from "../utils/axiosInstance";
import { addToBuyCart, addToRentCart, decreaseBuyQty, removeFromRentCart } from "../redux/cartSlice";
import { toggleWishlistItem, removeWishlistItem, addWishlistItem } from "../redux/wishlistSlice";

import Breadcrumbs from "./Breadcrumbs";

/* ── helpers ── */
const RENT_CONFIG_STORAGE_KEY = "rentConfigurations";

function loadStoredConfigs(variantId) {
  try {
    const all = JSON.parse(localStorage.getItem(RENT_CONFIG_STORAGE_KEY) || "{}");
    return Array.isArray(all[variantId]) ? all[variantId] : [];
  } catch {
    return [];
  }
}

function saveStoredConfigs(variantId, configs) {
  try {
    const all = JSON.parse(localStorage.getItem(RENT_CONFIG_STORAGE_KEY) || "{}");
    all[variantId] = configs;
    localStorage.setItem(RENT_CONFIG_STORAGE_KEY, JSON.stringify(all));
  } catch { /* ignore */ }
}

const formatMoney = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "Quote on request";
  return `$${number.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const stripHtml = (html = "") => html.replace(/<[^>]*>/g, "").trim();

const getImageSource = (image) => {
  if (!image) return "";
  if (typeof image === "string") return image;
  return image.image?.image || image.image || image.url || image.src || "";
};

const getProductImages = (product) => {
  const candidates = [
    product?.image,
    ...(Array.isArray(product?.images) ? product.images : []),
    ...(Array.isArray(product?.product_images) ? product.product_images : []),
    ...(Array.isArray(product?.gallery) ? product.gallery : []),
    ...(Array.isArray(product?.sub_images) ? product.sub_images : []),
  ];

  const seen = new Set();
  return candidates
    .map((image) => ({
      id: image?.id || getImageSource(image),
      name: image?.name || "Product image",
      src: getImageSource(image),
    }))
    .filter((image) => {
      if (!image.src || seen.has(image.src)) {
        return false;
      }
      seen.add(image.src);
      return true;
    });
};

const normalizeVariantDetail = (variant, seoProduct = {}) => {
  const baseProduct = variant?.product || {};
  const prices = variant?.prices || {};

  return {
    ...baseProduct,
    ...seoProduct,
    ...variant,
    brand: seoProduct.brand ?? baseProduct.brand,
    code: variant?.sku_code || baseProduct.code,
    description: seoProduct.description ?? baseProduct.description,
    discount_percentage: seoProduct.discount_percentage ?? baseProduct.discount_percentage,
    image: variant?.image || baseProduct.image,
    images: Array.isArray(variant?.images) ? variant.images : [],
    listing_type: variant?.varient_listing_type || baseProduct.listing_type,
    measurement_unit: seoProduct.measurement_unit ?? baseProduct.measurement_unit,
    product_id: baseProduct.id,
    rental_price: prices.rental_price ?? baseProduct.rental_price,
    sale_offer_price: prices.sale_offer_price ?? baseProduct.sale_offer_price,
    sale_price: prices.sale_price ?? baseProduct.sale_price,
  };
};

const ALL_CANONICAL_PLANS = [
  { key: "daily", label: "Daily", period: "Day" },
  { key: "weekly", label: "Weekly", period: "Week" },
  { key: "monthly", label: "Monthly", period: "Month" },
  { key: "annually", label: "Annually", period: "Year" },
];

const RENTAL_PLANS = ALL_CANONICAL_PLANS; // kept for backward compat

/**
 * Get the duration label for a saved config using the canonical ALL_PLANS mapping.
 * Matches by plan key directly, then falls back to a substring check against the label.
 */
const formatDurationDisplay = (duration, planKey) => {
  const key = (planKey || "").toLowerCase().trim();
  // Direct key match first
  let planMeta = ALL_PLANS.find((p) => p.key === key);
  // Fallback: substring match on key or label (handles "annually", "annual", "weekly", etc.)
  if (!planMeta) {
    planMeta = ALL_PLANS.find(
      (p) => key.includes(p.key) || key.includes(p.label.toLowerCase())
    );
  }
  // Last resort default to daily
  if (!planMeta) planMeta = ALL_PLANS[0];
  return `${duration} ${duration === 1 ? planMeta.unit : planMeta.unitPlural}`;
};

function ProductDetailPage({ productData = null }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { productSlug } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const variantId =
    searchParams.get("variant") ||
    location.state?.variantId ||
    productData?.variantId ||
    productData?.variant_id;
  const [product, setProduct] = useState(productData);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [show, setShow] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [pendingWishlistAfterLogin, setPendingWishlistAfterLogin] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState("");
  const [orderMode, setOrderMode] = useState(() => {
    const t = searchParams.get("type") || location.state?.type;
    return (t === "rent" || t === "rental") ? "rent" : "buy";
  });
  const [rentalPlan, setRentalPlan] = useState("daily");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showConfigureModal, setShowConfigureModal] = useState(false);
  const [rentConfigurations, setRentConfigurations] = useState([]);
  const [editingConfigId, setEditingConfigId] = useState(null);
  const [pricePackages, setPricePackages] = useState([]);

  /* Read rent cart from Redux (persisted to localStorage) */
  const rentCart = useSelector((store) => store.cart?.rentCart || []);
  const wishlistItems = useSelector((store) => store.wishlist?.items || []);
  const effectiveVariantId = product?.id || variantId;

  // Local wishlisted state that is type-aware (buy vs rent)
  const [wishlisted, setWishlisted] = useState(false);
  const [wishlistedId, setWishlistedId] = useState(null);

  // Derive from Redux (cross-component seeded) as fallback
  const isWishlisted = wishlisted || Boolean(
    effectiveVariantId &&
    wishlistItems.some(
      (item) => String(item.id) === String(effectiveVariantId) && item.type === orderMode
    )
  );

  useEffect(() => {
    if (variantId) {
      setLoading(true);
      setError(null);

      Promise.all([
        axiosInstance.get(`catlog/product-variant-detail/${variantId}/`),
        axiosInstance.get(`catlog/seo-url/${productSlug}/`),
      ])
        .then(([variantRes, seoRes]) => {
          if (seoRes.data?.product_type !== "product") {
            setError("Product not found.");
            setProduct(null);
            return;
          }

          setProduct(normalizeVariantDetail(variantRes.data, seoRes.data.product_data));
        })
        .catch(() => {
          setError("Failed to load product details. Please try again.");
          setProduct(null);
        })
        .finally(() => setLoading(false));
      return;
    }

    if (productData) {
      setProduct(productData);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    axiosInstance
      .get(`catlog/seo-url/${productSlug}/`)
      .then((res) => {
        if (res.data?.product_type === "product") {
          setProduct(res.data.product_data);
        } else {
          setError("Product not found.");
          setProduct(null);
        }
      })
      .catch(() => {
        setError("Failed to load product details. Please try again.");
        setProduct(null);
      })
      .finally(() => setLoading(false));
  }, [productData, productSlug, variantId]);

  // Seed wishlist state from server when product loads or orderMode changes
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userid");
    if (!token || !userId || !effectiveVariantId) return;

    // Check for current orderMode type (sale or rental for API, buy/rent for Redux)
    const apiType = orderMode === "rent" ? "rental" : "sale";

    axiosInstance
      .get("catlog/wishlist-check/", {
        params: { variant_id: effectiveVariantId, type: apiType },
      })
      .then((res) => {
        const inWishlist = res.data?.in_wishlist;
        const wId = res.data?.id;
        setWishlisted(!!inWishlist);
        setWishlistedId(wId || null);

        if (inWishlist) {
          // Seed Redux if not already present for this type
          const alreadyInStore = wishlistItems.some(
            (item) => String(item.id) === String(effectiveVariantId) && item.type === orderMode
          );
          if (!alreadyInStore) {
            dispatch(addWishlistItem({
              id: effectiveVariantId,
              name: product?.name,
              slug: product?.slug || productSlug,
              type: orderMode,
            }));
          }
        } else {
          setWishlisted(false);
          setWishlistedId(null);
        }
      })
      .catch(() => {
        setWishlisted(false);
        setWishlistedId(null);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVariantId, orderMode]);

  useEffect(() => {
    if (!effectiveVariantId) return;

    axiosInstance
      .get(`catlog/variant-price-packages/?product_variant=${effectiveVariantId}&active=true`)
      .then((res) => {
        setPricePackages(res.data?.results || []);
      })
      .catch(() => {
        setPricePackages([]);
      });
  }, [effectiveVariantId]);

  /* When the variant changes, restore configs from localStorage
     that still exist in the live rentCart (cart page removal = auto-clean) */
  useEffect(() => {
    if (!effectiveVariantId) return;
    const stored = loadStoredConfigs(effectiveVariantId);
    // Only keep configs whose cart items still exist in Redux rentCart
    const liveCartItemIds = new Set(rentCart.map((i) => i.cartItemId));
    const active = stored.filter((c) => liveCartItemIds.has(c.id));
    setRentConfigurations(active);
    setEditingConfigId(null);
    // If stored configs were pruned (cart item removed elsewhere), persist the cleaned list
    if (active.length !== stored.length) {
      saveStoredConfigs(effectiveVariantId, active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVariantId]);

  /* Keep config list in sync when rentCart changes from outside (Cart page removals) */
  useEffect(() => {
    if (!effectiveVariantId) return;
    const liveCartItemIds = new Set(rentCart.map((i) => i.cartItemId));
    setRentConfigurations((prev) => {
      const next = prev.filter((c) => liveCartItemIds.has(c.id));
      if (next.length !== prev.length) {
        saveStoredConfigs(effectiveVariantId, next);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rentCart]);

  const productImages = useMemo(() => getProductImages(product), [product]);
  const imageUrl = selectedImage || productImages[0]?.src || pm;
  const salePrice = formatMoney(product?.sale_offer_price || product?.sale_price);
  const hasDiscount = Number(product?.sale_price) > Number(product?.sale_offer_price) && Number(product?.sale_offer_price) > 0;
  const originalSalePrice = hasDiscount ? formatMoney(product?.sale_price) : null;
  const rentPrice = formatMoney(product?.rental_price);
  const description = stripHtml(product?.description || "");
  const shortDescription =
    description.length > 320
      ? `${description.slice(0, 320)}...`
      : description;

  const currentPlan = RENTAL_PLANS.find((p) => p.key === rentalPlan) || RENTAL_PLANS[1];
  const activePackage = getPackageForPlan(pricePackages, rentalPlan);
  const displayRentPrice = activePackage
    ? formatMoney(activePackage.offer_price)
    : rentPrice;
  const rentHasDiscount = activePackage && Number(activePackage.price) > Number(activePackage.offer_price) && Number(activePackage.offer_price) > 0;
  const originalRentPrice = rentHasDiscount ? formatMoney(activePackage.price) : null;
  const rentDiscountPercent = rentHasDiscount ? Math.round(Number(activePackage.discount_percent || ((activePackage.price - activePackage.offer_price) / activePackage.price) * 100)) : 0;

  const editingConfig = rentConfigurations.find((config) => config.id === editingConfigId);

  const getCartProduct = () => ({
    id: effectiveVariantId,
    name: product?.name,
    image: imageUrl,
    slug: product?.slug || productSlug,
    friendlyurl: product?.slug || productSlug,
    type: orderMode,
    offerPrice: orderMode === "buy"
      ? Number(product?.sale_offer_price || product?.sale_price || 0)
      : Number(product?.rental_price || 0),
    sale_offer_price: product?.sale_offer_price,
    sale_price: product?.sale_price,
    rental_price: product?.rental_price,
  });

  const handleQuote = () => {
    setSelectedProduct({
      id: product?.id,
      name: product?.name,
      image: imageUrl,
    });
    setShow(true);
  };

  const buyCart = useSelector((store) => store.cart?.buyCart || []);

  const buyItem = buyCart.find((item) => item.id === effectiveVariantId);
  const buyQty = buyItem ? buyItem.qty : 0;

  const handleDecrease = () => {
    dispatch(decreaseBuyQty(effectiveVariantId));
  };

  const buildCartItemId = (plan, duration, startDate) =>
    `${effectiveVariantId}-${plan}-${duration}-${startDate}-${Date.now()}`;

  const addRentConfigToCart = (configData, cartItemId) => {
    const cartProduct = getCartProduct();
    const safeQty = Math.max(1, Number(configData.qty) || 1);
    const offerPrice = configData.rentalCharges?.final_cost
      ? Number(configData.rentalCharges.final_cost) / safeQty
      : (configData.selectedPackage
        ? Number(configData.selectedPackage.offer_price) / Number(configData.selectedPackage.duration_value || 1)
        : Number(product?.rental_price || 0));
    const originalRentalPrice = configData.rentalCharges?.base_cost
      ? Number(configData.rentalCharges.base_cost) / safeQty
      : (configData.selectedPackage && Number(configData.selectedPackage.price) > Number(configData.selectedPackage.offer_price)
        ? Number(configData.selectedPackage.price) / Number(configData.selectedPackage.duration_value || 1)
        : Number(product?.rental_price || 0));

    const payload = {
      ...cartProduct,
      cartItemId,
      rentalPlan: configData.plan,
      duration: configData.duration,
      selectedPackage: configData.selectedPackage,
      offerPrice,
      rental_price: originalRentalPrice,
      fromDate: configData.startDate,
      toDate: configData.endDate || null,
      rentalCharges: configData.rentalCharges || null,
    };

    dispatch(addToRentCart(payload));
    for (let i = 1; i < configData.qty; i += 1) {
      dispatch(addToRentCart(payload));
    }
  };

  const handleConfigureSubmit = (configData) => {
    const planLabel = RENTAL_PLANS.find((p) => p.key === configData.plan)?.label || "Rental";
    const configEntry = {
      id: editingConfigId || buildCartItemId(configData.plan, configData.duration, configData.startDate),
      plan: configData.plan,
      qty: configData.qty,
      duration: configData.duration,
      startDate: configData.startDate,
      endDate: configData.endDate || null,
      price: configData.price,
      originalPrice: configData.originalPrice,
      discountPercent: configData.discountPercent,
      selectedPackage: configData.selectedPackage,
      rentalCharges: configData.rentalCharges || null,
      label: `${planLabel} Rental`,
    };

    if (editingConfigId) {
      dispatch(removeFromRentCart(editingConfigId));
      addRentConfigToCart(configData, configEntry.id);
      setRentConfigurations((prev) => {
        const next = prev.map((config) => (config.id === editingConfigId ? configEntry : config));
        saveStoredConfigs(effectiveVariantId, next);
        return next;
      });
      toast.success("Configuration updated");
    } else {
      addRentConfigToCart(configData, configEntry.id);
      setRentConfigurations((prev) => {
        const next = [...prev, configEntry];
        saveStoredConfigs(effectiveVariantId, next);
        return next;
      });
      toast.success("Configuration added");
    }

    setEditingConfigId(null);
    setShowConfigureModal(false);
  };

  const handleEditConfig = (configId) => {
    setEditingConfigId(configId);
    setShowConfigureModal(true);
  };

  const handleDeleteConfig = (configId) => {
    dispatch(removeFromRentCart(configId));
    setRentConfigurations((prev) => {
      const next = prev.filter((config) => config.id !== configId);
      saveStoredConfigs(effectiveVariantId, next);
      return next;
    });
    toast.success("Configuration removed");
  };

  const handleCloseConfigureModal = () => {
    setShowConfigureModal(false);
    setEditingConfigId(null);
  };

  const handleAddToCart = () => {
    if (orderMode === "rent") {
      setEditingConfigId(null);
      setShowConfigureModal(true);
      return;
    }

    dispatch(addToBuyCart(getCartProduct()));
    toast.success("Added to cart");
  };

  const handleViewCart = () => {
    navigate("/cart");
  };

  const handleToggleWishlist = async () => {
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userid");

    if (!token || !userId) {
      setPendingWishlistAfterLogin(true);
      setShowLogin(true);
      return;
    }

    if (wishlistLoading) return;
    setWishlistLoading(true);

    try {
      const apiType = orderMode === "rent" ? "rental" : "sale";
      const reduxType = orderMode; // "buy" or "rent" — for Redux state

      if (isWishlisted && wishlistedId) {
        // Remove using the stored wishlistedId (no need to re-check)
        await axiosInstance.delete(`catlog/wishlists/${wishlistedId}/`);
        setWishlisted(false);
        setWishlistedId(null);
        dispatch(removeWishlistItem({ id: effectiveVariantId, type: reduxType }));
        window.dispatchEvent(new Event("wishlistUpdated"));
        toast.success("Removed from wishlist");
      } else {
        // Double-check from server first to avoid duplicates
        const checkRes = await axiosInstance.get("catlog/wishlist-check/", {
          params: { variant_id: effectiveVariantId, type: apiType },
        });
        const inWishlist = checkRes.data?.in_wishlist;
        const existingId = checkRes.data?.id;

        if (inWishlist && existingId) {
          // Already exists — just update local state
          setWishlisted(true);
          setWishlistedId(existingId);
          toast.info("Already in wishlist");
        } else {
          const postRes = await axiosInstance.post("catlog/wishlists/", {
            user: userId,
            varient: effectiveVariantId,
            type: apiType,
          });
          setWishlisted(true);
          setWishlistedId(postRes.data?.id || null);
          dispatch(addWishlistItem({
            id: effectiveVariantId,
            name: product?.name,
            slug: product?.slug || productSlug,
            type: reduxType,
          }));
          window.dispatchEvent(new Event("wishlistUpdated"));
          toast.success("Saved to wishlist");
        }
      }
    } catch {
      toast.error("Could not update wishlist. Please try again.");
    } finally {
      setWishlistLoading(false);
    }
  };

  const doAddToWishlistApi = async () => {
    const userId = localStorage.getItem("userid");
    if (!userId || !effectiveVariantId) return;
    setWishlistLoading(true);
    try {
      const apiType = orderMode === "rent" ? "rental" : "sale";
      const reduxType = orderMode; // "buy" or "rent"

      const checkRes = await axiosInstance.get("catlog/wishlist-check/", {
        params: { variant_id: effectiveVariantId, type: apiType },
      });

      const inWishlist = checkRes.data?.in_wishlist;
      const existingId = checkRes.data?.id;

      if (!inWishlist) {
        const postRes = await axiosInstance.post("catlog/wishlists/", {
          user: userId,
          varient: effectiveVariantId,
          type: apiType,
        });
        setWishlisted(true);
        setWishlistedId(postRes.data?.id || null);
        dispatch(addWishlistItem({
          id: effectiveVariantId,
          name: product?.name,
          slug: product?.slug || productSlug,
          type: reduxType,
        }));
        window.dispatchEvent(new Event("wishlistUpdated"));
        toast.success("Saved to wishlist");
      } else {
        setWishlisted(true);
        setWishlistedId(existingId || null);
        toast.success("Already in wishlist");
      }
    } catch {
      toast.error("Could not update wishlist. Please try again.");
    } finally {
      setWishlistLoading(false);
    }
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
    if (pendingWishlistAfterLogin) {
      setPendingWishlistAfterLogin(false);
      doAddToWishlistApi();
    }
  };

  useEffect(() => {
    setSelectedImage(productImages[0]?.src || "");
  }, [productImages]);

  const validImages = productImages.length > 0 ? productImages : [{ id: "fallback", name: product?.name, src: pm }];
  const currentImageIndex = validImages.findIndex((img) => img.src === imageUrl);

  const handlePrevImage = () => {
    if (validImages.length <= 1) return;
    const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : validImages.length - 1;
    setSelectedImage(validImages[newIndex].src);
  };

  const handleNextImage = () => {
    if (validImages.length <= 1) return;
    const newIndex = currentImageIndex < validImages.length - 1 ? currentImageIndex + 1 : 0;
    setSelectedImage(validImages[newIndex].src);
  };

  // Derive the short description from product description (first sentence or two)
  const shortDescText = (() => {
    if (!description) return "";
    const sentences = description.match(/[^.!?]+[.!?]+/g) || [];
    return sentences.slice(0, 2).join(" ").trim() || description.slice(0, 120);
  })();

  return (
    <main className="pd-page">
      <div className="pd-wrap">
        {/* Breadcrumbs */}
        <Breadcrumbs items={[
          { label: "Home", path: "/" },
          { label: orderMode === "rent" ? "Rentals" : "Buy", path: orderMode === "rent" ? "/rent" : "/buy" },
          { label: product?.name || "Product", path: "" }
        ]} />

        {loading && (
          <div className="pd-center">
            <div className="pd-spinner" role="status" />
          </div>
        )}

        {!loading && error && (
          <div className="pd-error">{error}</div>
        )}

        {!loading && !error && product && (
          <div className="pd-shell">

            {/* ── LEFT — light image panel ── */}
            <div className="pd-left">
              <div className="pd-left-inner">

                {/* Brand / model label */}
                <div className="pd-brand-block">
                  <div className="pd-brand-logo">
                    {product.brand?.brand_name || "SITE-EXIT"}
                  </div>
                  {product.code && (
                    <>
                      <div className="pd-model-label">
                        {product.subcategory_name?.[0] || "Product"}
                      </div>
                      <div className="pd-model-code">Model {product.code}</div>
                    </>
                  )}
                </div>

                {/* Discount chip */}
                {/* {product.discount_percentage && (
                  <div className="pd-chips">
                    <span className="pd-chip pd-chip-deal">{product.discount_percentage}% OFF</span>
                  </div>
                )} */}

                {/* Main image */}
                <div className="pd-img-frame">
                  {validImages.length > 1 && (
                    <button className="pd-img-nav prev" onClick={handlePrevImage} aria-label="Previous image">
                      <FiChevronLeft />
                    </button>
                  )}
                  <img
                    src={imageUrl}
                    alt={product.name}
                    onError={(e) => { e.target.src = pm; }}
                  />
                  {validImages.length > 1 && (
                    <button className="pd-img-nav next" onClick={handleNextImage} aria-label="Next image">
                      <FiChevronRight />
                    </button>
                  )}
                </div>

                {/* Thumbnails */}
                <div className="pd-thumbs" aria-label="Product images">
                  {(productImages.length > 0 ? productImages : [{ id: "fallback", name: product.name, src: pm }]).map((image, index) => (
                    <button
                      key={`${image.id}-${index}`}
                      type="button"
                      className={`pd-thumb${imageUrl === image.src ? " active" : ""}`}
                      onClick={() => setSelectedImage(image.src)}
                      aria-label={`View image ${index + 1}`}
                    >
                      <img
                        src={image.src}
                        alt=""
                        onError={(e) => { e.target.src = pm; }}
                      />
                    </button>
                  ))}
                </div>

                {/* Wishlist button */}
                <button
                  className={`pd-wish-btn${isWishlisted ? " active" : ""}${wishlistLoading ? " loading" : ""}`}
                  type="button"
                  onClick={handleToggleWishlist}
                  disabled={wishlistLoading}
                  aria-label={isWishlisted ? "Remove from wishlist" : "Save to wishlist"}
                >
                  {isWishlisted ? <FaHeart color="#fe7900" /> : <FiHeart color="currentColor" />}
                  <span>
                    {wishlistLoading
                      ? "Updating..."
                      : isWishlisted
                        ? "Saved to wishlist"
                        : "Save to wishlist"}
                  </span>
                </button>

              </div>
            </div>

            {/* ── RIGHT — info panel ── */}
            <div className="pd-right">

              {/* Product title */}
              <div className="pd-title-block">
                <h1 className="pd-name">{product.name}</h1>
                {shortDescText && (
                  <p className="pd-short-desc">{shortDescText}</p>
                )}
              </div>

              {/* Full description (expandable) */}
              {description && description.length > shortDescText.length && (
                <div style={{ marginBottom: 16 }}>
                  {showFullDescription && (
                    <p className="pd-desc-text" style={{ color: "#555", fontSize: 13.5, lineHeight: 1.8, marginBottom: 6 }}>
                      {description}
                    </p>
                  )}
                  {description.length > 120 && (
                    <button
                      type="button"
                      className="pd-read-more"
                      onClick={() => setShowFullDescription((prev) => !prev)}
                    >
                      {showFullDescription ? "Read Less" : "Read More"}
                    </button>
                  )}
                </div>
              )}

              {/* Spec grid */}
              <div className="pd-spec-row">
                {Array.isArray(product?.options) && product.options.length > 0 ? (
                  product.options.map((option, index) => (
                    <div className="pd-spec-item" key={index}>
                      <span className="pd-spec-label">{option.variant_type}</span>
                      <span className="pd-spec-val">{option.variant_option}</span>
                    </div>
                  ))
                ) : (
                  <>
                    <div className="pd-spec-item">
                      <span className="pd-spec-label">Model Code</span>
                      <span className="pd-spec-val">{product.code || "N/A"}</span>
                    </div>
                    <div className="pd-spec-item">
                      <span className="pd-spec-label">Category</span>
                      <span className="pd-spec-val">{product.subcategory_name?.[0] || "N/A"}</span>
                    </div>
                    {product.brand?.brand_name && (
                      <div className="pd-spec-item">
                        <span className="pd-spec-label">Brand</span>
                        <span className="pd-spec-val">{product.brand.brand_name}</span>
                      </div>
                    )}
                    {product.measurement_unit && (
                      <div className="pd-spec-item">
                        <span className="pd-spec-label">Unit</span>
                        <span className="pd-spec-val">{product.measurement_unit}</span>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* BUY / RENT toggle */}
              <div className="pd-purchase-panel">
                <div className="pd-mode-wrapper">
                  <div className="pd-mode-toggle" aria-label="Select purchase type">
                    <label className={`pd-mode-option${orderMode === "buy" ? " active" : ""}`}>
                      <input
                        type="radio"
                        name="orderMode"
                        value="buy"
                        checked={orderMode === "buy"}
                        onChange={() => setOrderMode("buy")}
                      />
                      <span className="pd-mode-copy"><strong>BUY</strong></span>
                    </label>
                    <label className={`pd-mode-option${orderMode === "rent" ? " active" : ""}`}>
                      <input
                        type="radio"
                        name="orderMode"
                        value="rent"
                        checked={orderMode === "rent"}
                        onChange={() => setOrderMode("rent")}
                      />
                      <span className="pd-mode-copy"><strong>RENT</strong></span>
                    </label>
                  </div>
                </div>

                {/* Rental plan selector — hidden once a configuration has been made */}
                {orderMode === "rent" && rentConfigurations.length === 0 && (
                  <>
                    <div className="pd-rental-plan-label">Rental Plan</div>
                    <div className="pd-rental-plan-pills">
                      {ALL_CANONICAL_PLANS.map((plan) => {
                        const isAvailable = pricePackages.some(
                          (pkg) => (pkg.package_name || "").toLowerCase() === plan.label.toLowerCase()
                        );
                        const pkg = pricePackages.find(
                          (p) => (p.package_name || "").toLowerCase() === plan.label.toLowerCase()
                        );
                        return (
                          <button
                            key={plan.key}
                            type="button"
                            className={`pd-pill${rentalPlan === plan.key ? " active" : ""}${!isAvailable ? " unavailable" : ""}`}
                            onClick={() => isAvailable && setRentalPlan(plan.key)}
                            disabled={!isAvailable}
                            title={!isAvailable ? `${plan.label} plan not available` : ""}
                          >
                            {/* {rentalPlan === plan.key && isAvailable && (
                              <span className="pd-pill-check">
                                <FiCheck />
                              </span>
                            )} */}
                            <span className="pd-pill-label">{plan.label}</span>
                            {/* {isAvailable && pkg && (
                              <span className="pd-pill-price">
                                ${Number(pkg.offer_price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                {Number(pkg.price) > Number(pkg.offer_price) && (
                                  <del style={{ fontSize: "11px", color: "#888", fontWeight: 400, marginLeft: "5px" }}>
                                    ${Number(pkg.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </del>
                                )}
                              </span>
                            )} */}
                            {!isAvailable && (
                              <span className="pd-pill-na">N/A</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}

                {/* Price display */}
                <div className="pd-price-cards">
                  {orderMode === "buy" ? (
                    <div className="pd-price-card pd-buy-card">
                      <div className="pd-price-row">
                        <strong className="pd-amount">{salePrice}</strong>
                        {hasDiscount && (
                          <del className="pd-original">{originalSalePrice}</del>
                        )}
                        {Number(product.discount_percentage) > 0 && hasDiscount && (
                          <span className="pd-save">{product.discount_percentage}% off</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="pd-price-card pd-rent-card">
                      <div className="pd-price-row">
                        <strong className="pd-amount">{displayRentPrice}</strong>
                        <span className="pd-price-period">/ {currentPlan.period}</span>
                        {rentHasDiscount && (
                          <del className="pd-original">{originalRentPrice}</del>
                        )}
                        {rentDiscountPercent > 0 && rentHasDiscount && (
                          <span className="pd-save">{rentDiscountPercent}% off</span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {orderMode === "rent" && rentConfigurations.length > 0 && (
                <div className="pd-config-section">
                  <h3 className="pd-config-heading">Configuration</h3>
                  {rentConfigurations.map((config) => (
                    <div className="pd-config-card" key={config.id}>
                      <div className="pd-config-accent" />
                      <div className="pd-config-body">
                        <div className="pd-config-main">
                          <span className="pd-config-tag">{config.plan.toUpperCase()}</span>
                          {/* <span className="pd-config-title">{config.label}</span> */}
                        </div>
                        <div className="pd-config-meta">
                          <div className="pd-config-meta-item">
                            <span className="pd-config-meta-label">Qty</span>
                            <span className="pd-config-meta-value">{String(config.qty).padStart(2, "0")}</span>
                          </div>
                          <div className="pd-config-meta-item">
                            <span className="pd-config-meta-label">Duration</span>
                            <span className="pd-config-meta-value">
                              {formatDurationDisplay(config.duration, config.plan)}
                            </span>
                          </div>
                          <div className="pd-config-meta-item">
                            <span className="pd-config-meta-label">Start Date</span>
                            <span className="pd-config-meta-value">
                              {config.startDate}
                            </span>
                          </div>
                          <div className="pd-config-price-wrapper" style={{ textAlign: "right" }}>
                            <div className="pd-config-price">{formatMoney(config.price)}</div>
                            {/* {config.originalPrice && Number(config.originalPrice) > Number(config.price) && (
                              <div style={{ fontSize: "12px", color: "#888", marginTop: "-2px" }}>
                                <del>{formatMoney(config.originalPrice)}</del>
                                {Number(config.discountPercent) > 0 && (
                                  <span style={{ color: "#e74c3c", fontWeight: 600, marginLeft: "4px" }}>
                                    ({Math.round(Number(config.discountPercent))}% off)
                                  </span>
                                )}
                              </div>
                            )} */}
                          </div>

                        </div>
                      </div>
                      <div className="pd-config-actions">
                        <button
                          type="button"
                          className="pd-config-action-btn"
                          onClick={() => handleEditConfig(config.id)}
                          aria-label="Edit configuration"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          type="button"
                          className="pd-config-action-btn"
                          onClick={() => handleDeleteConfig(config.id)}
                          aria-label="Delete configuration"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="pd-config-add-btn"
                    onClick={() => {
                      setEditingConfigId(null);
                      setShowConfigureModal(true);
                    }}
                  >
                    + Add Another Configuration
                  </button>
                </div>
              )}

              {/* Add to Cart / Quantity stepper */}
              <div className="pd-actions">
                {orderMode === "buy" ? (
                  buyQty > 0 ? (
                    <div className="pd-qty-stepper">
                      <button
                        type="button"
                        className="pd-qty-btn"
                        onClick={handleDecrease}
                      >
                        −
                      </button>

                      <div className="pd-qty-display">{buyQty}</div>

                      <button
                        type="button"
                        className="pd-qty-btn"
                        onClick={handleAddToCart}
                      >
                        +
                      </button>
                    </div>
                  ) : (
                    <button
                      className="pd-cart-cta"
                      type="button"
                      onClick={handleAddToCart}
                    >
                      Add to Cart
                    </button>
                  )
                ) : (
                  rentConfigurations.length === 0 && (
                    <button
                      className="pd-cart-cta"
                      type="button"
                      onClick={handleAddToCart}
                    >
                      Add to Cart
                    </button>
                  )
                )}
              </div>

            </div>
          </div>
        )}

        <FormModal selectedProduct={selectedProduct} show={show} setShow={setShow} />
        <ConfigureRentalModal
          show={showConfigureModal}
          onHide={handleCloseConfigureModal}
          product={{
            id: effectiveVariantId,
            variant_id: effectiveVariantId,
            name: product?.name,
            image: imageUrl,
            code: product?.code,
            subcategory_name: product?.subcategory_name,
            rental_price: product?.rental_price,
          }}
          initialPlan={editingConfig?.plan || rentalPlan}
          initialQty={editingConfig?.qty || 1}
          initialDuration={editingConfig?.duration || 1}
          initialStartDate={editingConfig?.startDate || ""}
          packages={pricePackages}
          onSubmit={handleConfigureSubmit}
        />
        <LoginModal
          show={showLogin}
          onHide={() => { setShowLogin(false); setPendingWishlistAfterLogin(false); }}
          onLoginSuccess={handleLoginSuccess}
        />
      </div>
    </main>
  );
}

export default ProductDetailPage;
